import { useState, useEffect } from 'react';
import { adminApiClient } from '../lib/api';
import { getAssetUrl } from '../lib/utils';

interface WebsiteConfig {
  siteName: string;
  logo: string;
  favicon: string;
  bannerImage: string;
  settings?: {
    theme?: {
      primaryColor?: string;
      secondaryColor?: string;
      tertiaryColor?: string;
      neutralColor?: string;
      fontFamily?: string;
      accentColor?: string;
      tableHeaderColor?: string;
      sidebarBgColor?: string;
    };
  };
  contactInfo: {
    email: string;
    phone: string;
  };
  integrations?: {
    googleMapsApiKey?: string;
  };
}

export const useWebsiteConfig = () => {
  const [config, setConfig] = useState<WebsiteConfig | null>(null);
  const [loading, setLoading] = useState(false); // Start with false to prevent initial loading
  const [error, setError] = useState<string | null>(null);

  const fetchConfig = async () => {
    try {
      setLoading(true);
      setError(null);
      const token = localStorage.getItem('admin_auth_token');
      const response = token
        ? await adminApiClient.getWebsiteConfig()
        : await adminApiClient.getPublicWebsiteConfig();
      if (response.success && response.data) {
        setConfig(response.data.config);
      } else {
        setError(response.message || 'Failed to fetch website configuration');
      }
    } catch (err: any) {
      if (err.response?.status !== 401) {
        setError(err.message || 'Failed to fetch website configuration');
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchConfig();
  }, []);

  // Helper function to construct image URLs
  const getImageUrl = (imagePath: string) => {
    if (!imagePath) return '';
    if (imagePath.startsWith('http')) return imagePath;
    return getAssetUrl(imagePath);
  };

  return {
    config,
    loading,
    error,
    getImageUrl,
    refetch: fetchConfig,
  };
};
